/** Connection-capacity card — how often the member's network is at its plan's
 *  speed limit, with the 7×24 heatmap. Same shared components as the admin
 *  surface (@nodus/capacity); the backend scopes the data to the member's own
 *  network. Moved from `pages/network.tsx` when the page split into tabs
 *  (#310); renders on the Performance tab beside Speed Test — one measures the
 *  connection on demand, the other describes how it has actually behaved.
 *
 *  It used to return null whenever no provider covered the network, which is
 *  the common case — so on most networks the card simply was not there, with
 *  nothing to say why. Now it keeps its place and explains itself, exactly as
 *  Speed Test already does when it is unavailable off-site. */
import { Gauge } from "lucide-react";
import { Box, Card, Group, Stack, Text } from "@mantine/core";
import { CapacityBadge, CapacityStaleBadge, CapacitySummary, CapacityHeatmap } from "@nodus/capacity";
import { useNetworkCapacity } from "@/hooks/use-network-queries";

export function CapacityCard({ hasNetwork = true }: { hasNetwork?: boolean }) {
  const { data, isLoading } = useNetworkCapacity();

  const header = (
    <Group justify="space-between" mb="sm">
      <Group gap="xs">
        <Gauge size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
        <Text fw={500}>Connection capacity</Text>
      </Group>
      {data?.has_provider && (
        <Group gap={6} wrap="nowrap">
          <CapacityBadge verdict={data.verdict} size="md" />
          {/* Beside the verdict, same placement as the admin dashboard's
              "closest to plan limit" card (#343): `verdict` bands the minutes
              that WERE measured and never asks how long ago they arrived, so
              a member whose sampler died days ago would otherwise see a
              confident, unqualified verdict here. */}
          {data.stale && <CapacityStaleBadge newestSampleAt={data.newest_sample_at} />}
        </Group>
      )}
    </Group>
  );

  // Not yet known: say nothing rather than flash an empty state that is about
  // to be replaced by a verdict.
  if (isLoading) {
    return (
      <Card withBorder padding="lg">
        {header}
        <Text fz="sm" c="dimmed" ta="center" py="xs">Loading…</Text>
      </Card>
    );
  }

  // No provider covers this network, or the request failed. Both mean "nothing
  // to show", and neither is something a member can act on — so one calm line
  // rather than an error.
  //
  // The card also renders for a tenant with NO network at all. Saying "not set
  // up for your network" there would describe a network they do not have — the
  // Connect tab's own "No network yet" card is the real answer.
  if (!data || !data.has_provider) {
    return (
      <Card withBorder padding="lg">
        {header}
        <Text fz="sm" c="dimmed" ta="center" py="xs">
          {hasNetwork
            ? "Capacity monitoring isn't set up for your network yet"
            : "Available once your space has a network"}
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg">
      {header}
      <Stack gap="md">
        <CapacitySummary data={data} />
        <Box>
          <Text fz="sm" fw={600} mb={6}>
            Saturated minutes per hour, last 7 days ({data.site_time_zone})
          </Text>
          <CapacityHeatmap
            grid={data.grid}
            measuredGrid={data.measured_grid}
            sampledGrid={data.sampled_grid}
            cellVerdicts={data.cell_verdicts}
            // Plan changes as ticks at the hour they happened (#313 phase 3).
            // Both fields are optional on the payload, so an older backend
            // simply renders no markers. No prices here — money stays on
            // Connect.
            speedChanges={data.speed_changes?.map((c) => ({
              dayIndex: c.day_index,
              hour: c.hour,
              downloadMbps: c.download_mbps,
              uploadMbps: c.upload_mbps,
              direction: c.direction,
              at: c.at,
            }))}
            windowStartPlan={
              data.window_start_plan
                ? {
                    downloadMbps: data.window_start_plan.download_mbps,
                    uploadMbps: data.window_start_plan.upload_mbps,
                  }
                : undefined
            }
          />
        </Box>
      </Stack>
    </Card>
  );
}
